import React, { FC } from 'react';
import { ButtonGroup, ButtonToolbar, Col, Row } from 'reactstrap';
import {
  BaseUserWithAddress,
  UserAllRegistrations,
  UserRegistration,
} from '../../../shared/user/base';
import { LinkButton, MaterialIcon, MessageCard } from '../../components';
import { useTranslation } from '../../translations';
import { Fetcher, useConvention, useFetcher, useUserSafe } from '../../utils';
import { RegistrationListItem } from './listitem';

interface RegistrationListProps {
  readonly allRegistrations: UserAllRegistrations;
  readonly refresh: () => void;
}

export const RegistrationList: FC<RegistrationListProps> = ({ allRegistrations, refresh }) => {
  const { limitAttendees } = useConvention();
  const request = useFetcher(async () => {
    let limitReached = false;

    if (limitAttendees) {
      const countReq = await api.getRegistrationLimitReached();
      ({ limitReached } = countReq);
    }

    return {
      limitReached,
    };
  }, [limitAttendees]);

  if (!request.success) {
    return <Fetcher result={request} />;
  }

  return (
    <EventRegistration
      allRegistrations={allRegistrations}
      limitAttendees={limitAttendees}
      limitReached={request.data.limitReached}
      refresh={refresh}
    />
  );
};

interface EventRegistrationProps {
  readonly limitReached: boolean;
  readonly allRegistrations: UserAllRegistrations;
  readonly refresh: () => void;
  readonly limitAttendees: number | null;
}

const EventRegistration: FC<EventRegistrationProps> = ({
  limitReached,
  allRegistrations,
  refresh,
  limitAttendees,
}) => {
  const user = useUserSafe();
  const { ts } = useTranslation();
  const { maxSubAccounts } = useConvention();
  const { registrations, users } = allRegistrations;
  const canCreateMore = registrations.length === 0 || users.length - 1 < maxSubAccounts;

  if (registrations.length === 0 && limitAttendees && limitReached) {
    return (
      <MessageCard icon="remove_circle" id="registrationFull" level="danger">
        {ts('registration_is_full')}
      </MessageCard>
    );
  }

  return (
    <>
      <ButtonToolbar>
        <ButtonGroup className="me-2">
          <LinkButton
            color="primary"
            disabled={!canCreateMore}
            id="createNewRegistration"
            to={
              registrations.length === 0 ? `/event/register/edit/${user.id}` : '/event/register/new'
            }
          >
            {ts('create_new_registration')}
          </LinkButton>
        </ButtonGroup>
      </ButtonToolbar>
      <hr />
      <Row className="justify-content-center" id="conventionInfoForm">
        {registrations.length > 0 ? (
          <RenderRegistrationItem refresh={refresh} registrations={registrations} users={users} />
        ) : (
          <NoRegistration />
        )}
      </Row>
    </>
  );
};

const RenderRegistrationItem: FC<{
  readonly registrations: UserRegistration[];
  readonly users: BaseUserWithAddress[];
  readonly refresh: () => void;
}> = ({ registrations, users, refresh }) => {
  const user = useUserSafe();
  const otherUsers = users.filter((t) => t.id !== user.id);
  const usersWithoutReg = otherUsers.filter((t) => !registrations.some((r) => r.userId === t.id));

  return (
    <Col lg={8} xs={12}>
      <RegistrationListItem
        conRegistration={registrations.find((t) => t.userId === user.id)}
        refresh={refresh}
        user={users.find((t) => t.id === user.id)!}
      />
      {registrations
        .filter((t) => t.userId !== user.id)
        .map((reg) => {
          const regUser = otherUsers.find((t) => t.id === reg.userId);

          if (!regUser) {
            return null;
          }

          return (
            <RegistrationListItem
              conRegistration={reg}
              key={user.id}
              refresh={refresh}
              user={regUser}
            />
          );
        })}
      {usersWithoutReg.map((regUser) => {
        return <RegistrationListItem key={user.id} refresh={refresh} user={regUser} />;
      })}
    </Col>
  );
};

const NoRegistration: FC = () => {
  const { ts } = useTranslation();
  return (
    <Col className="text-center" lg={8} xs={12}>
      <MaterialIcon large name="sentiment_dissatisfied" />
      <h4>{ts('you_dont_have_any_registrations')}</h4>
      <p>{ts('lets_get_you_registered_just')}</p>
    </Col>
  );
};
